import axios, { AxiosError, AxiosResponse } from "axios";

import { useEffect } from "react";
import useAuthStore, { UserModelType } from "../context/useAuthStore";

import axiosRetry from "axios-retry";
import FormData from "form-data";
import { toast } from "sonner";
import {
  ActivateAICredits,
  ActivateAICreditsResponse,
} from "../components/ActivateAICreditsPage";
import {
  GetCompletedDates,
  GetCompletedDatesResult,
} from "../components/CompleteEntryPage";
import {
  DownloadEntriesModel,
  DownloadEntriesResult,
  GetOnlineEntriesModel,
  GetOnlineEntriesResult,
} from "../components/DownloadEntries";
import { UpdateEntryResult } from "../components/EntryAccordion";
import {
  GetFilledReportsRequest,
  GetFilledReportsResult,
} from "../components/ExportPage";
import { CreatePaymentIntentResult } from "../components/PurchasePackagesPage";
import { CustomMetaData, ProductModel } from "../components/PurchasePage";
import { DateRangeModel } from "../components/RandomizeEntriesWizard";
import {
  RegisterResult,
  UserRegistrationModel,
} from "../components/RegisterPage";
import { ReportSectionSortOrders } from "../components/ReportAccordion";
import { UpdateTemplateResult } from "../components/TemplateSelection";
import { Folders } from "../components/desktop/DesktopDrawer";
import {
  DeleteCompletedEntryModel,
  DeleteCompletedEntryResult,
} from "../components/modals/DeleteCompletedEntryModal";
import {
  DeleteEntryModel,
  DeleteEntryResult,
} from "../components/modals/DeleteEntryModal";
import {
  DeleteEntriesModel,
  DeleteEntriesResult,
} from "../components/modals/DeleteSelectedEntriesModal";
import { UploadProfilePhotoResponse } from "../components/modals/EditAccountModal";
import {
  UpdateEntryTypeModel,
  UpdateEntryTypeResult,
} from "../components/modals/UpdateCompletedEntryModal";
import {
  CreateEntryResult,
  CreateFolderResult,
  DeleteFolderResult,
  EntryModel,
  FolderModel,
  GetPackagesResponse,
  GetProductsResponse,
  GetReportSectionsResult,
  GetTemplatesResult,
  LoginResult,
  NewEntriesModel,
  NewEntriesResult,
  NewEntryModel,
  PartialUpdateCompletedEntryModel,
  ReportSection,
  ReportType,
  UpdateEntryModel,
  UpdateFolderRequest,
  UpdateFolderResult,
  UpdateTemplateModel,
} from "../context/BerichtsheftContext";
import { useNavigationContext } from "../context/NavigationContext";
import {
  Errors,
  GetAISuggestionsRequest,
  NewAIRequestResponse,
  SavedResponse,
} from "./useAISuggestions";
import useDateUtils from "./useDateUtils";
import {
  GetFilledReportRequest,
  GetFilledReportResult,
} from "./useFilledReport";
import {
  GetFilledReportSectionRequest,
  GetFilledReportSectionResult,
} from "./useFilledReportSection";
import { useQuery } from "@tanstack/react-query";

const currentHostname = window.location.hostname;
export const baseUrl =
  process.env.REACT_APP_NODE_ENV == "production"
    ? "https://berichtsheft-generator.ai:443"
    : process.env.REACT_APP_NODE_ENV == "staging"
    ? "https://staging.berichtsheft-generator.ai:443"
    : `http://${currentHostname}:8181`;

export const baseAIUrl =
  process.env.REACT_APP_NODE_ENV == "production"
    ? "https://berichtsheft-generator.ai:8555"
    : process.env.REACT_APP_NODE_ENV == "staging"
    ? "https://staging.berichtsheft-generator.ai:8555"
    : `http://${currentHostname}:8555`;
//const baseUrl = process.env.REACT_APP_NODE_ENV == "production" ?  "https://berichtsheft-generator.ai:443" : "http://localhost:8181";
export const baseUrlPayment =
  process.env.REACT_APP_NODE_ENV == "production"
    ? "https://berichtsheft-generator.ai:8444"
    : process.env.REACT_APP_NODE_ENV == "staging"
    ? "https://staging.berichtsheft-generator.ai:8444"
    : `http://${currentHostname}:8444`;

export const payPalClientId =
  process.env.REACT_APP_NODE_ENV == "production"
    ? process.env.REACT_APP_PAYPAL_CLIENT_ID
    : process.env.REACT_APP_NODE_ENV == "staging"
    ? process.env.REACT_APP_PAYPAL_CLIENT_ID_STAGING
    : process.env.REACT_APP_PAYPAL_CLIENT_ID_DEV;

export const stripeClientId =
  process.env.REACT_APP_NODE_ENV == "production"
    ? process.env.REACT_APP_STRIPE_CLIENT_ID
    : process.env.REACT_APP_NODE_ENV == "staging"
    ? process.env.REACT_APP_STRIPE_CLIENT_ID_STAGING
    : process.env.REACT_APP_STRIPE_CLIENT_ID_DEV;

export default function useHttpClient() {
  const ipAddressOrHostname = baseUrl;
  const ipAddressOrHostnamePayment = baseUrlPayment;
  const ipAddressOrHostnameAI = baseAIUrl;
  const client = axios.create({ baseURL: ipAddressOrHostname });
  const paymentClient = axios.create({ baseURL: ipAddressOrHostnamePayment });
  const aiClient = axios.create({ baseURL: ipAddressOrHostnameAI });
  const dateUtils = useDateUtils();
  const { goToLoginPage, goToPurchasePackagesPage } = useNavigationContext();
  axiosRetry(client, {
    retries: 3,
  });

  const {
    accessToken,
    refreshToken,
    setRefreshToken,
    setAccessToken,
    user,
    setUser,
    logout,
  } = useAuthStore();

  useEffect(() => {}, [accessToken, refreshToken]);

  //data class UploadProfilePhotoResponse(val errorList: List<Error>, val user: UserModel?)

  function uploadProfilePhoto(
    file: File,
    onSuccess: (data: UploadProfilePhotoResponse) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    const data = new FormData();
    data.append("file", file, file.name);

    client
      .put("/protected/user/avatar", data, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response: AxiosResponse<UploadProfilePhotoResponse>) {
        onSuccess(response.data);
      })
      .catch(function (error: AxiosError<UploadProfilePhotoResponse>) {
        if (isAccessTokenExpiredError(error?.response?.data?.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              uploadProfilePhoto(file, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data?.errorList);
            });
        } else {
          onFail(error?.response?.data?.errorList || []);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function getAISuggestions(
    requestId: string,
    token = accessToken,
    retryCount = 0
  ): Promise<SavedResponse> {
    return aiClient
      .get<SavedResponse>(`/protected/getAISuggestions/${requestId}`, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError<Errors>) => {
        //console.log("Error", error);
        if (
          isAccessTokenExpiredError(error?.response?.data?.errorList) &&
          retryCount < 3
        ) {
          // retry limit set to 3
          return attemptToRefreshToken()
            .then((newToken: string) => {
              return getAISuggestions(requestId, newToken, retryCount + 1);
            })
            .catch((error) => {
              return error.response.data.errorList;
            });
        } else {
          return Promise.reject(error);
        }
      });
  }
  function newAIRequest(
    params: GetAISuggestionsRequest,
    token = accessToken,
    retryCount = 0
  ): Promise<NewAIRequestResponse> {
    return client
      .post<NewAIRequestResponse>("/protected/ai/new-request", params, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: AxiosError<NewAIRequestResponse>) => {
        if (error.response?.status === 400) {
          return Promise.reject(error.response?.data?.errorList);
        }

        if (error.response?.status === 401 && retryCount < 3) {
          return attemptToRefreshToken()
            .then((newToken) => {
              return newAIRequest(params, newToken, retryCount + 1);
            })
            .catch((error: AxiosError<NewAIRequestResponse>) => {
              // Handle token refresh errors specifically (if any)
              return Promise.reject(error.response?.data?.errorList);
            });
        }

        if (error?.response?.status === 402) {
          const errorMessage = error?.response?.data?.errorList[0]?.error;

          toast.warning(errorMessage, {
            action: {
              label: "Aufladen",
              onClick: () => goToPurchasePackagesPage(),
            },
          });
          return Promise.reject(error.response?.data?.errorList);
        }

        return Promise.reject(error.response?.data?.errorList);
      });
  }
  //   @Serializable
  // data class CreateEntryResult(val errorList: List<Error>, val entry: EntryModel? = null)

  function createEntry(
    entry: NewEntryModel,
    token = accessToken
  ): Promise<CreateEntryResult> {
    return client
      .post("/protected/createEntry", entry, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((token) => {
              createEntry(entry, token);
            })
            .catch((error) => {
              return error.response.data.errorList;
            });
        } else {
          return error.response.data.errorList;
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  //@Serializable
  //data class NewEntriesModel(val entries: List<NewEntryModel>)
  /**
   * @Serializable
data class NewEntryModel(
    val name: String,
    val description: String,
    val reportSectionName: String,
    val folderId: Int,
    val maxHours: Double,
    val visible: Boolean,
    val userId: String,
    val type: String,
    val sortOrder: Int,
    val dates: Array<Long>,
    val isRandomEntry: Boolean = false
)

   */

  function createEntries(
    entries: NewEntriesModel,
    onSuccess: (data: NewEntriesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post<NewEntriesResult>("/protected/createEntries", entries, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              createEntries(entries, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  type UpdateReportSectionMaxHours = {
    reportSectionId: number;
    maxHours: number;
    enabled: boolean;
  };

  type UpdateReportSectionMaxHoursResult = {
    errorList: Errors["errorList"];
  };

  function updateMaxHours(
    params: UpdateReportSectionMaxHours,
    onSuccess: (data: UpdateReportSectionMaxHoursResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post<UpdateReportSectionMaxHoursResult>(
        "/protected/updateMaxHours",
        params,
        {
          headers: {
            authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateMaxHours(params, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data?.errorList || []);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function updateEntryType(
    request: UpdateEntryTypeModel,
    onSuccess: (data: UpdateEntryTypeResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/updateEntryType", request, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateEntryType(request, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        }
      });
  }

  function updateFolder(
    folder: UpdateFolderRequest,
    onSuccess: (data: UpdateFolderResult) => void,
    onFail: (data: UpdateFolderResult) => void,
    token = accessToken
  ) {
    client
      .post<UpdateFolderResult>("/protected/updateFolder", folder, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateFolder(folder, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data);
            });
        } else {
          onFail(error.response.data);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function updateEntry(
    entry: UpdateEntryModel,
    onSuccess: (data: UpdateEntryResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/updateEntry", entry, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateEntry(entry, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function downloadEntries(
    downloadEntriesModel: DownloadEntriesModel,
    onSuccess: (data: DownloadEntriesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/downloadEntries", downloadEntriesModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              downloadEntries(
                downloadEntriesModel,
                onSuccess,
                onFail,
                response
              );
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function deleteEntries(
    deleteEntriesModel: DeleteEntriesModel,
    onSuccess: (data: DeleteEntriesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/deleteEntries", deleteEntriesModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              deleteEntries(deleteEntriesModel, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function deleteEntry(
    deleteEntryModel: DeleteEntryModel,
    onSuccess: (data: DeleteEntryResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/deleteEntry", deleteEntryModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              deleteEntry(deleteEntryModel, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function deleteCompletedEntry(
    deleteCompletedEntryModel: DeleteCompletedEntryModel,
    onSuccess: (data: DeleteCompletedEntryResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/deleteCompletedEntry", deleteCompletedEntryModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              deleteCompletedEntry(
                deleteCompletedEntryModel,
                onSuccess,
                onFail,
                response
              );
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  type UpdateCompletedEntryResult = {
    errorList: Errors["errorList"];
    entry?: EntryModel;
  };

  function updateCompletedEntry(
    entry: PartialUpdateCompletedEntryModel,
    onSuccess: (data: UpdateCompletedEntryResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/updateCompletedEntry", entry, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateCompletedEntry(entry, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  /**
   
@Serializable
data class ReportSectionSortOrders(val reportSectionName: String, val entrySortOrders: List<EntrySortOrder>)


data class EntrySortOrder(val entryId: Int, val sortOrder: Int)

   */

  function sortEntries(
    entrySortOrder: ReportSectionSortOrders,
    onSuccess: (data: unknown) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/sortEntries", entrySortOrder, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              sortEntries(entrySortOrder, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, sortEntries(entrySortOrder,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function createFolder(
    folder: FolderModel,
    onSuccess: (data: CreateFolderResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/createFolder", folder, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              createFolder(folder, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createFolder(folder,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function getOnlineEntries(
    parameters: GetOnlineEntriesModel,
    onSuccess: (data: GetOnlineEntriesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/getOnlineEntries", parameters, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              getOnlineEntries(parameters, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createFolder(folder,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  // A function to make the network request. It returns a promise.
  async function fetchOnlineEntries(
    parameters: GetOnlineEntriesModel,
    token = accessToken
  ): Promise<GetOnlineEntriesResult> {
    try {
      const response = await client.post<GetOnlineEntriesResult>(
        "/protected/getOnlineEntries",
        parameters,
        {
          headers: {
            authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      const errorList = errorResponse?.errorList || [];

      if (isAccessTokenExpiredError(errorList)) {
        const newToken = await attemptToRefreshToken();
        return fetchOnlineEntries(parameters, newToken); // Retry the request with refreshed token
      } else {
        throw errorList; // Propagate error to be handled by useQuery
      }
    }
  }

  function useGetOnlineEntries(parameters: GetOnlineEntriesModel) {
    const response = useQuery<GetOnlineEntriesResult, Errors["errorList"]>({
      queryFn: async () => await fetchOnlineEntries(parameters),
      queryKey: ["getOnlineEntries", parameters],
    });

    return {
      ...response,
      incomingEntries: response.data?.entries || [],
    };
  }

  function deleteFolder(
    folder: FolderModel,
    onSuccess: (data: DeleteFolderResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/deleteFolder", folder, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              deleteFolder(folder, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, deleteFolder(folder,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function updateTemplate(
    updateTemplateModel: UpdateTemplateModel,
    onSuccess: (data: UpdateTemplateResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/updateTemplate", updateTemplateModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              updateTemplate(updateTemplateModel, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, generateLicense(onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  type CompletedTutorialResponse = {
    errorList: Errors["errorList"];
    user: UserModelType;
  };
  function setCompletedTutorial(
    onSuccess: (data: CompletedTutorialResponse) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/setCompletedTutorial", null, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              setCompletedTutorial(onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, generateLicense(onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  // '/create-payment-intent'
  function createPaymentIntent(
    intent: {
      items: ProductModel[];
      emailAddress: string;
      customMetaData: CustomMetaData;
    },
    onSuccess: (data: CreatePaymentIntentResult) => void,
    onFail: (errors: unknown) => void
  ) {
    paymentClient
      .post("/create-payment-intent", JSON.stringify(intent))
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (errors) {
        onFail(errors);
      });
  }

  function updatePaymentIntent(
    intent: {
      items: ProductModel[];
      emailAddress: string;
      clientSecret: String;
    },
    onSuccess: (data: unknown) => void,
    onFail: (errors: unknown) => void
  ) {
    paymentClient
      .post("/update-payment-intent", JSON.stringify(intent))
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        onFail(error.response.data.errorList);
      });
  }

  type UserLicenseKeyModel = {
    id: number;
    validKey: string;
    activationDate: number | null;
    generationDate: number;
    active: boolean;
    customerId: string | null;
    userId: string | null;
    version: string;
    type: string;
  };
  type GenerateLicenseKeyResult = {
    errorList: Errors["errorList"];
    licenseKey: UserLicenseKeyModel;
  };

  function generateLicense(
    onSuccess: (data: GenerateLicenseKeyResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post(
        "/protected/generateLicense",
        {
          accessToken: token,
        },
        {
          headers: {
            authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      )
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              generateLicense(onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, generateLicense(onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  /**
   * 
   * @param randomizeEntriesModel @Serializable
data class RandomizeEntriesModel(
    val entries: List<EntryModel>,
    val dates: List<DateRangeModel>,
    val selectedReportSections: List<ReportSectionModel>,
    val countPerReportSection: Int
)

@Serializable
data class RandomizeEntriesResult(val folderId: Int?, val errorList: List<Error>)

   */

  type RandomizeEntriesModel = {
    entries: EntryModel[];
    dates: DateRangeModel[];
    selectedReportSections: ReportSection[];
    countPerReportSection: number;
  };

  type RandomizeEntriesResult = {
    folderId: number | null;
    errorList: Errors["errorList"];
  };

  function randomize(
    randomizeEntriesModel: RandomizeEntriesModel,
    onSuccess: (data: RandomizeEntriesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/randomize", randomizeEntriesModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              randomize(randomizeEntriesModel, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, generateLicense(onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  type LoginCredentials = {
    username: string;
    password: string;
  };

  function login(
    userCredentials: LoginCredentials,
    onSuccess: (data: LoginResult) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) {
    client
      .post<LoginResult>("/login", {
        username: userCredentials.username,
        password: userCredentials.password,
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        onFail(error.response.data?.errorList ?? []);
      });
  }

  function isAccessTokenExpiredError(
    errorList: Errors["errorList"] | undefined
  ) {
    if (!errorList) return false;
    return (
      errorList?.length > 0 &&
      errorList[0].error == "Access Token ist abgelaufen."
    );
  }

  function attemptToRefreshToken() {
    return client
      .post<LoginResult>(
        "/protected/refreshAccessToken",
        {},
        {
          headers: {
            authorization: `Bearer ${refreshToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      )
      .catch(function (error) {
        logout();
        console.log("failed to refresh token!", error);
        return Promise.reject(error);
      })
      .then(function (response) {
        //DO NOT CHECK USE CHECK ACCESS TOKEN EXPIRATION ERROR FUNCTION HERE (WOULD CALL ITSELF UPON FAILURE REPEATEDLY)
        const accessToken = response.data.tokenPair.accessToken;
        setAccessToken(accessToken);
        setRefreshToken(response.data.tokenPair.refreshToken);
        setUser(response.data.user);
        //console.log("Refreshed Token", accessToken);
        return accessToken;
        // onSuccess();
      });
  }

  // val startDate = getFilledReportRequest.startDate
  // val endDate = getFilledReportRequest.endDate
  // val selectedReport = getFilledReportRequest.selectedReport
  // val newLine = getFilledReportRequest.newLine
  // val horizontalLine = getFilledReportRequest.horizontalLine
  // val username = principal!!.payload.getClaim("username").asString()

  function getCompletedDates(
    params: GetCompletedDates,
    onSuccess: (data: GetCompletedDatesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/getCompletedDates", params, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              getCompletedDates(params, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error,setEntriesAsCompleted(setEntriesAsCompletedModel,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  /**
   * @Serializable
data class SetEntryAsCompletedModel(
    val id: Int,
    val reportSectionName: String,
    val sortOrder: Int,
    val dates: Array<Long>,
    val datesToDelete: Array<Long>,
    val isRandomEntry: Boolean
)

   */
  type SetEntryAsCompletedModel = {
    id: number;
    reportSectionName: string;
    sortOrder: number;
    dates: number[];
    datesToDelete: number[];
    isRandomEntry: boolean;
  };
  type SetEntriesAsCompletedModel = {
    completedEntries: SetEntryAsCompletedModel[];
  };

  function setEntriesAsCompleted(
    setEntriesAsCompletedModel: SetEntriesAsCompletedModel,
    onSuccess: (data: unknown) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/completeEntries", setEntriesAsCompletedModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              setEntriesAsCompleted(
                setEntriesAsCompletedModel,
                onSuccess,
                onFail,
                response
              );
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error,setEntriesAsCompleted(setEntriesAsCompletedModel,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function getFilledReport(
    request: GetFilledReportRequest,
    token = accessToken
  ): Promise<GetFilledReportResult> {
    return client
      .post<GetFilledReportResult>("/protected/getFilledReport", request, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              getFilledReport(request, response);
            })
            .catch((error) => {
              return error.response.data?.errorList ?? [];
            });
        } else {
          return error.response.data.errorList;
        } //checkForAccessTokenExiprationError(error, getFilledReport(startDate,endDate,newLine,horizontalLine,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  function getFilledReportSection(
    request: GetFilledReportSectionRequest,
    token = accessToken
  ): Promise<GetFilledReportSectionResult> {
    return client
      .post<GetFilledReportResult>(
        "/protected/getFilledReportSection",
        request,
        {
          headers: {
            authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      )
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((token) => {
              getFilledReportSection(request, token);
            })
            .catch((error) => {
              return error.response.data.errorList;
            });
        } else {
          return error.response.data.errorList;
        } //checkForAccessTokenExiprationError(error, getFilledReport(startDate,endDate,newLine,horizontalLine,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  /**
   * 
   @Serializable
data class GetFilledReportsRequest(val dates: List<DateRangeModel>)
@Serializable
data class GetFilledReportsResult(
    val errorList: List<Error>, val userTemplate: UserTemplateModel? = null,
    val reportSections: List<ReportSectionModel>? = null, val filledReportModels: List<FilledReportModel>? = null,
)

   */

  function getFilledReports(
    getFilledReportsRequestModel: GetFilledReportsRequest,
    onSuccess: (data: GetFilledReportsResult) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .post("/protected/getFilledReports", getFilledReportsRequestModel, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              getFilledReports(
                getFilledReportsRequestModel,
                onSuccess,
                onFail,
                response
              );
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, getFilledReport(startDate,endDate,newLine,horizontalLine,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  /**
   * @Serializable
data class GetTemplatesResult(val templates : List<TemplateOption>)
@Serializable
data class TemplateOption(val reportTypeId : Int, val name : String, val reportSections : List<ReportSectionModel>)
@Serializable
data class GetProductsResponse(
    val errorList: List<Error>,
    val products: List<ProductModel>
)

@Serializable
data class GetPackagesResponse(
    val packages: List<ProductModel>,
    val errorList: List<Error>,
)
@Serializable
data class ReportTypeModel(val id: Int, val name: String)

@Serializable
data class GetReportSectionsResult(val errorList: List<Error>, val reportSections: List<ReportSectionModel>? = null)
@Serializable
data class Folders(val folders: List<FolderModel>)
   */

  const getTemplates = (
    onSuccess: (data: GetTemplatesResult) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/templates", onSuccess, onFail);

  const getProducts = (
    onSuccess: (data: GetProductsResponse) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/products", onSuccess, onFail);

  const getPackages = (
    onSuccess: (data: GetPackagesResponse) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/packages", onSuccess, onFail);

  const getReportTypes = (
    onSuccess: (data: ReportType[]) => void,
    onFail: (errors: unknown) => void
  ) => get("/protected/reportTypes", onSuccess, onFail);

  const getReportSections = (
    onSuccess: (data: GetReportSectionsResult) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/protected/reportSections", onSuccess, onFail);

  const getAllReportSections = (
    onSuccess: (data: GetReportSectionsResult) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/protected/allReportSections", onSuccess, onFail);

  function get<T>(
    url: string,
    onSuccess: (data: T) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    client
      .get(url, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              get(url, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error,get(url,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  async function getProtectedBlob(url: string): Promise<Blob> {
    //const accessToken = localStorage.getItem("accessToken"); // Retrieve the access token
    if (!accessToken) {
      throw new Error("No access token available.");
    }

    const fetchData = async (token: string): Promise<Blob> => {
      const response = await fetch(`${baseUrl}${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/octet-stream", // or keep 'application/json' if typically JSON
        },
      });

      if (!response.ok) {
        // Assuming 401 indicates token expiration; adjust as necessary
        if (response.status === 401) {
          const refreshedToken = await attemptToRefreshToken();
          return fetchData(refreshedToken); // Retry fetch with the new token
        }
        // Handle other errors
        throw new Error("Network response was not ok.");
      }
      return response.blob();
    };

    return fetchData(accessToken);
  }

  async function getProtected<T>(url: string): Promise<T> {
    //const accessToken = localStorage.getItem("accessToken"); // Retrieve the access token

    if (!accessToken) {
      throw new Error("No access token available.");
    }
    const fetchData = async (token: string): Promise<T> => {
      const response = await fetch(`${baseUrl}${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        // Assuming 401 indicates token expiration; adjust as necessary
        if (response.status === 401) {
          const refreshedToken = await attemptToRefreshToken();
          return fetchData(refreshedToken); // Retry fetch with the new token
        }
        // Handle other errors
        throw new Error("Network response was not ok.");
      }
      return response.json();
    };

    return fetchData(accessToken);
  }

  async function postProtected<T, R>(url: string, data: T): Promise<R> {
    //const accessToken = localStorage.getItem("accessToken"); // Retrieve the access token
    if (!accessToken) {
      throw new Error("No access token available.");
    }

    const fetchData = async (token: string): Promise<R> => {
      const response = await fetch(`${baseUrl}${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        // Assuming 401 indicates token expiration; adjust as necessary
        if (response.status === 401) {
          const refreshedToken = await attemptToRefreshToken();
          return fetchData(refreshedToken); // Retry fetch with the new token
        }

        throw new Error("Network response was not ok.");
      }
      return response.json();
    };

    return fetchData(accessToken);
  }

  function activateAICredits(
    request: ActivateAICredits,
    onSuccess: (data: ActivateAICreditsResponse) => void,
    onFail: (errorList: Errors["errorList"]) => void,
    token = accessToken
  ) {
    return client
      .post("/protected/activate-credits", request, {
        headers: {
          authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        if (isAccessTokenExpiredError(error.response.data.errorList)) {
          attemptToRefreshToken()
            .then((response) => {
              activateAICredits(request, onSuccess, onFail, response);
            })
            .catch((error) => {
              onFail(error.response.data.errorList);
            });
        } else {
          onFail(error.response.data.errorList);
        } //checkForAccessTokenExiprationError(error, createEntry(entry,onSuccess,onFail),onFail(error.response.data.errorList));
      });
  }

  const getFolders = (
    onSuccess: (data: Folders) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) => get("/protected/folders", onSuccess, onFail);

  /**
 * 
 * @Serializable
data class UserRegistrationModel(
    val username: String,
    val password: String,
    val name: String,
    val lastName: String,
    val emailAddress: String,
    val startDate: Long,
    val endDate: Long,
    val jobId: Int,
    val reportTypeId: Int,
    val companyName: String,
    val licenseKey: String,
    val template: String,
)
@Serializable
data class RegisterResult(val errorList: List<Error>, val tokenPair: TokenPair? = null, val user: UserModel? = null)


 */

  function register(
    userRegistrationModel: UserRegistrationModel,
    onSuccess: (data: RegisterResult) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) {
    return client
      .post("/register", userRegistrationModel)
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        onFail(error.response.data.errorList);
      });
  }

  /**
   * @Serializable
data class ForgotPasswordRequest(
    val emailAddress: String
)

@Serializable
data class NewPasswordRequest(
    val token : String,
    val password : String,
)

   */
  type ForgotPasswordRequest = {
    emailAddress: string;
  };
  type NewPasswordRequest = {
    token: string;
    password: string;
  };
  function forgot(
    forgotModel: ForgotPasswordRequest,
    onSuccess: (data: unknown) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) {
    client
      .post("/forgot", forgotModel)
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        onFail(error.response.data.errorList);
      });
  }

  function changePassword(
    newPasswordModel: NewPasswordRequest,
    onSuccess: (data: unknown) => void,
    onFail: (errorList: Errors["errorList"]) => void
  ) {
    client
      .post("/changePassword", newPasswordModel)
      .then(function (response) {
        onSuccess(response.data);
      })
      .catch(function (error) {
        onFail(error.response.data.errorList);
      });
  }

  /**
   * @Serializable
data class UserExistsRequest(val emailAddress: String)

   */

  function checkIfUserExists(emailAddress: string): Promise<boolean> {
    return client
      .post("/userExists", { emailAddress })
      .then(function (response) {
        return response.data;
      });
  }

  return {
    client,
    baseUrl,
    ipAddressOrHostname,
    checkIfUserExists,
    uploadProfilePhoto,
    changePassword,
    forgot,
    createEntries,
    setCompletedTutorial,
    getCompletedDates,
    getProducts,
    getPackages,
    createPaymentIntent,
    updatePaymentIntent,
    updateTemplate,
    updateMaxHours,
    getTemplates,
    downloadEntries,
    deleteEntry,
    deleteEntries,
    deleteCompletedEntry,
    getOnlineEntries,
    login,
    generateLicense,
    randomize,
    register,
    getFolders,
    createFolder,
    deleteFolder,
    getFilledReport,
    getFilledReportSection,
    getFilledReports,
    createEntry,
    updateFolder,
    updateEntry,
    updateEntryType,
    updateCompletedEntry,
    getReportSections,
    getAllReportSections,
    getReportTypes,
    sortEntries,
    setEntriesAsCompleted,
    newAIRequest,
    activateAICredits,
    getAISuggestions,
    get,
    getProtected,
    getProtectedBlob,
    postProtected,
    attemptToRefreshToken,
    isAccessTokenExpiredError,
    useGetOnlineEntries,
  };
}
